// ============================================================
// FILE: src/pages/About.jsx  →  ABOUT PAGE
// Sections: Our Story, Vision & Mission, Core Values,
// Leadership team, Company Timeline, Global Presence.
// TIP: team photos/names, values and timeline text are in
//      src/data/site.js — edit them there.
// ============================================================

import { Link } from "react-router-dom";
import { ArrowRight, Eye, Target, Globe2 } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { VALUES, LEADERSHIP, REGIONS, CERTIFICATIONS, SERVICES } from "../data/site";

export default function About() {
  useSEO("About Us — Evolve Trade & Logistics", "Our story, vision, mission, values and leadership. Building trusted trade corridors across 40+ countries since 2011.");
  return (
    <>
      <PageHero
        label="About Evolve"
        title="Built on trade lanes,"
        accent="measured in trust"
        description="From a two-person trading desk in Mumbai to a trade network spanning six regions — the story of Evolve Trade & Logistics."
        crumbs={[{ label: "About" }]}
      />

      <section className="py-16 sm:py-24" data-testid="story-section">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] border border-slate-900/10 dark:border-white/10">
                <img
                  src="/images/team/about-story.jpg"
                  alt="Cargo vessel at sea during sunset"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div className="glass absolute -bottom-6 -right-4 rounded-2xl px-6 py-4 sm:-right-6" data-testid="story-badge">
                <p className="font-display text-3xl font-bold text-gradient">40+</p>
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Countries Served</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="section-label">Our Story</p>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              Trade corridors built on <span className="text-gradient">trust</span>
            </h2>
            {/* ===== OUR STORY TEXT ===== */}
            {/* Edit the two paragraphs below to change the company story. */}
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Evolve Trade &amp; Logistics was built on a lesson learned moving commodities across borders: in international trade, the product is only half the promise. The other half is verification, documentation and delivery — the discipline that decides whether a container earns money or costs it.
              </p>
              <p>
                Today we coordinate sourcing, inspection, documentation and logistics across more than 40 countries — agriculture and spices, construction materials, industrial goods, textiles and metals. But the operating principle has never changed: every lot inspected, every document verified, every milestone met.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="vision-mission-section">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="card-surface h-full" data-testid="vision-card">
              <Eye className="h-9 w-9 text-teal-500" />
              <h3 className="mt-5 font-display text-2xl font-semibold">Our Vision</h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                To be the most trusted trade partner between emerging-market producers and global buyers — where every transaction strengthens both sides of the corridor.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="card-surface h-full" data-testid="mission-card">
              <Target className="h-9 w-9 text-teal-500" />
              <h3 className="mt-5 font-display text-2xl font-semibold">Our Mission</h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                To make global sourcing as reliable as buying locally — verified suppliers, inspected quality, first-time-right documentation and delivery you can plan around.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="values-section">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="section-label">Core Values</p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">What we <span className="text-gradient">refuse to compromise</span></h2>
          </Reveal>
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="card-surface h-full !p-6" data-testid={`value-card-${i}`}>
                  <span className="font-display text-sm font-bold tracking-widest text-teal-500">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="leadership-section">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="section-label">Leadership</p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">The people behind <span className="text-gradient">the promise</span></h2>
          </Reveal>
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {LEADERSHIP.map((p) => (
              <StaggerItem key={p.name}>
                <div className="group" data-testid={`leader-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="relative overflow-hidden rounded-2xl border border-slate-900/10 dark:border-white/10">
                    <img src={p.image} alt={p.name} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-display text-lg font-semibold text-white">{p.name}</h3>
                      <p className="text-xs font-medium uppercase tracking-widest text-teal-300">{p.role}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.bio}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ===== OUR EXPERTISE (SERVICES) SECTION ===== */}
      {/* Professional overview of what the company does — replaces the old timeline. */}
      <section className="py-16 sm:py-24" data-testid="expertise-section">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="section-label">Our Expertise</p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              What we do, <span className="text-gradient">done properly</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Twelve integrated trade services, one accountable team. Every engagement is led by a dedicated specialist and measured against a single standard: your cargo, delivered as promised.
            </p>
          </Reveal>
          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <div className="card-surface group flex h-full items-start gap-4 !p-6" data-testid={`expertise-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700/15 to-teal-500/15">
                    <s.icon className="h-5 w-5 text-teal-500" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold leading-snug">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10">
            <Link to="/services" className="btn-secondary" data-testid="expertise-services-link">
              Explore All Services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="global-presence-section">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="section-label">Global Presence</p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Six regions, <span className="text-gradient">one standard</span></h2>
          </Reveal>
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REGIONS.map((r) => (
              <StaggerItem key={r.name}>
                <div className="card-surface h-full !p-6" data-testid={`region-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Globe2 className="h-6 w-6 text-teal-500" />
                  <h3 className="mt-4 font-display text-lg font-semibold">{r.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.blurb}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {r.countries.slice(0, 4).map((c) => <span key={c} className="chip !px-2.5 !py-1 !text-[10px]">{c}</span>)}
                    {r.countries.length > 4 && <span className="chip !px-2.5 !py-1 !text-[10px]">+{r.countries.length - 4} more</span>}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-14 text-center">
            <Link to="/contact" className="btn-primary" data-testid="about-cta">Work With Us <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
